import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import {
  Layers,
  Users,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Server
} from 'lucide-react';
import PageHeader from '@components/layout/page-header';
import StatWidget from '@components/back-office/shared/stat-widget';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { useGetAdminQueue } from '@utils/api/admin/use-get-admin-queue';
import { QueueInfo } from '@utils/types/admin';

/**
 * On-Prem Queue Status Page
 * Displays RabbitMQ connection status, queue depths, and consumer counts.
 */
export default function AdminQueue() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const { data, isLoading, isFetching, dataUpdatedAt, refetch, isError } =
    useGetAdminQueue();

  const queueStatus = data?.result;
  const queues = queueStatus?.queues ?? [];

  // Format last updated time
  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : null;

  // Helper to format numbers with locale
  const formatNumber = (num: number | undefined): string => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString();
  };

  // Table columns for queues
  const columns = useMemo<DataGridColumn<QueueInfo>[]>(
    () => [
      {
        id: 'name',
        header: t('queue-name'),
        field: 'name',
        accessorKey: 'name',
        minWidth: 200,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => (
          <div className="flex items-center gap-2">
            <Server
              className="h-4 w-4 flex-shrink-0"
              style={{ color: getColour('inactiveText') }}
            />
            <span
              className="font-medium font-mono text-sm"
              style={{ color: getColour('normalText') }}
            >
              {String(getValue() ?? '')}
            </span>
          </div>
        )
      },
      {
        id: 'depth',
        header: t('depth'),
        field: 'depth',
        accessorKey: 'depth',
        minWidth: 120,
        maxWidth: 150,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => {
          const depth = getValue() as number;
          const isHigh = depth > 100;
          return (
            <span
              className={`text-sm font-medium ${isHigh ? 'text-amber-500 dark:text-amber-400' : ''}`}
              style={isHigh ? undefined : { color: getColour('normalText') }}
            >
              {formatNumber(depth)}
            </span>
          );
        }
      },
      {
        id: 'consumers',
        header: t('consumers'),
        field: 'consumers',
        accessorKey: 'consumers',
        minWidth: 120,
        maxWidth: 150,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => {
          const consumers = getValue() as number;
          const hasConsumers = consumers > 0;
          return (
            <span
              className="text-sm font-medium"
              style={{
                color: hasConsumers
                  ? getColour('normalText')
                  : getColour('inactiveText')
              }}
            >
              {formatNumber(consumers)}
            </span>
          );
        }
      }
    ],
    [t, getColour]
  );

  if (isError) {
    return (
      <div className="px-4 py-2">
        <PageHeader
          title={t('queue-status')}
          caption={t('rabbitmq-connection-and-queue-metrics')}
        />
        <div
          className="rounded-2xl border p-8 text-center"
          style={{
            borderColor: getColour('border'),
            backgroundColor: getColour('secondLayer')
          }}
        >
          {}
          <XCircle className="h-12 w-12 mx-auto mb-4 text-red-500 dark:text-red-400" />
          <p className="text-sm" style={{ color: getColour('normalText') }}>
            {t('failed-to-load-queue-status')}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 rounded-2xl text-sm font-medium"
            style={{
              backgroundColor: getColour('thirdLayer'),
              color: getColour('normalText')
            }}
          >
            {t('retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-app min-h-screen px-4 py-2">
      <div className="flex items-start justify-between mb-2">
        <PageHeader
          title={t('queue-status')}
          caption={t('rabbitmq-connection-and-queue-metrics')}
        />

        {/* Auto-refresh indicator */}
        <div className="flex items-center gap-2 text-xs mt-2">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1 px-2 py-1 rounded-2xl transition-colors"
            style={{
              color: getColour('inactiveText'),
              backgroundColor: isFetching
                ? getColour('thirdLayer')
                : 'transparent'
            }}
            title={t('refresh')}
          >
            <RefreshCw
              className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`}
            />
            {lastUpdated && (
              <span style={{ color: getColour('inactiveText') }}>
                {lastUpdated}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <section className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Connection Status */}
          <StatWidget
            title={t('connection')}
            value={queueStatus?.connected ? t('connected') : t('disconnected')}
            subtitle={t('rabbitmq-status')}
            icon={
              queueStatus?.connected ? (
                <CheckCircle2 className="h-5 w-5" />
              ) : (
                <XCircle className="h-5 w-5" />
              )
            }
            variant={queueStatus?.connected ? 'success' : 'danger'}
            loading={isLoading}
          />

          {/* Total Depth */}
          <StatWidget
            title={t('total-depth')}
            value={formatNumber(queueStatus?.total_depth)}
            subtitle={t('messages-in-queue')}
            icon={<Layers className="h-5 w-5" />}
            variant={
              (queueStatus?.total_depth ?? 0) > 100 ? 'warning' : 'default'
            }
            loading={isLoading}
          />

          {/* Total Consumers */}
          <StatWidget
            title={t('total-consumers')}
            value={formatNumber(queueStatus?.total_consumers)}
            subtitle={t('active-workers')}
            icon={<Users className="h-5 w-5" />}
            variant={
              (queueStatus?.total_consumers ?? 0) === 0 ? 'danger' : 'success'
            }
            loading={isLoading}
          />
        </div>
      </section>

      {/* Queues Table */}
      <section>
        <h2
          className="text-sm font-semibold mb-4 uppercase tracking-wide flex items-center gap-2"
          style={{ color: getColour('inactiveText') }}
        >
          <Server className="h-4 w-4" />
          {t('queues')}
        </h2>

        {!isLoading && queues.length === 0 ? (
          <div
            className="rounded-2xl border p-8 text-center"
            style={{
              borderColor: getColour('border'),
              backgroundColor: getColour('secondLayer')
            }}
          >
            <Layers
              className="h-12 w-12 mx-auto mb-4"
              style={{ color: getColour('inactiveText') }}
            />
            <p className="text-sm" style={{ color: getColour('inactiveText') }}>
              {t('no-queues-found')}
            </p>
          </div>
        ) : (
          <div
            className="rounded-2xl overflow-hidden"
            style={{ backgroundColor: getColour('baseLayer') }}
          >
            <DataGridV2<QueueInfo>
              data={queues}
              columns={columns}
              enableRowSelection={false}
              enablePagination={false}
              enableSorting
              manualSorting={false}
              enableFiltering={false}
              height="auto"
              dense
              grid={false}
              striped
              loading={isLoading || isFetching}
              getRowId={row => row.name}
            />
          </div>
        )}
      </section>
    </div>
  );
}
